/**
 * Root redirect for authenticated users. Picks the first workspace the
 * user belongs to and navigates into it, or sends them to `/no-workspace`
 * if their membership list is empty (edge case — only triggers when the
 * user's only workspace was deleted).
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/functional-design/frontend-components.md § route guards` (`<RootRedirect>`).
 *
 * NOTE: this component mounts under `<RequireAuth>`, so by the time it
 * runs the user is guaranteed `authenticated`. The `/api/me` response
 * already includes a workspace (the current session's), so we navigate
 * straight there without an extra `listWorkspaces()` round-trip in the
 * common case. The query runs anyway to handle the rare zero-workspace
 * edge case correctly.
 */

import { authClient, useAuth, type Permission } from "@cometchat-ai/auth/client";
import { Spinner } from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";

import { apiRequest } from "../agents/api/client";
import { camelizeKeys } from "../tables/api/camelize";
import { onboardingKeys, type OnboardingOverview } from "../onboarding/api/onboarding";

/**
 * Platform-staff bypass (design WP-6, B1/B2 slice 4): an FDE / internal
 * operator always gets the full dashboard even on an onboarding-mode
 * workspace — now governed by the `platform:staff` role (in the principal's
 * permissions, B1), replacing the prior `@cometchat.com` email heuristic.
 * Routing-only.
 */
function isPlatformStaff(permissions: ReadonlyArray<Permission>): boolean {
  return permissions.includes("platform:staff");
}

/**
 * A pure helpdesk operator (helpdesk_member / helpdesk_admin): can work the
 * desk queue but is not a builder/admin. Used only to pick their landing
 * surface. Permission-based so it survives the singular-`role` claim being
 * narrowed to admin/member.
 */
function isSupportOperator(permissions: ReadonlyArray<Permission>): boolean {
  return permissions.includes("handoff:claim") && !permissions.includes("agent:read");
}

export function RootRedirect() {
  const auth = useAuth();
  const { t } = useTranslation("auth");
  const [searchParams] = useSearchParams();

  const { data: workspaces, isPending } = useQuery({
    queryKey: ["workspaces", "list"],
    queryFn: () => authClient.listWorkspaces(),
    staleTime: 30_000,
  });

  // Read the surface flag so onboarding-mode workspaces land on `/onboard`.
  // Only meaningful once a workspace session exists.
  const slug = auth.workspace?.slug ?? "";
  const { data: overview, isPending: overviewPending } = useQuery({
    queryKey: onboardingKeys.overview(slug),
    queryFn: () =>
      apiRequest<unknown>("GET", "/api/onboarding/overview").then(camelizeKeys<OnboardingOverview>),
    enabled: Boolean(auth.workspace),
    staleTime: 30_000,
  });

  // The managed-Airbyte OAuth callback 302s to `/?connector=…`. Bounce back to
  // the connectors page the connect was started from, preserving the result.
  const connectorResult = searchParams.get("connector");
  if (connectorResult) {
    const returnTo = sessionStorage.getItem("tables.oauthReturn");
    sessionStorage.removeItem("tables.oauthReturn");
    if (returnTo) {
      return <Navigate to={`${returnTo}?connector=${connectorResult}`} replace />;
    }
  }

  if (auth.workspace) {
    // Wait for the surface flag before deciding (avoids a dashboard flash).
    if (overviewPending) {
      return (
        <div className="flex min-h-dvh items-center justify-center">
          <Spinner aria-label={t("bootstrap.loading")} />
        </div>
      );
    }
    // A pure support operator works the helpdesk — land them on the inbox,
    // and do so BEFORE the onboarding gate: the setup wizard is for
    // builders/admins, so a desk-only operator (`handoff:claim` without
    // `agent:read`) must never be trapped in `/onboard`. Detected by
    // permissions (robust to how the role string is typed).
    if (isSupportOperator(auth.permissions)) {
      return <Navigate to={`/w/${auth.workspace.slug}/helpdesk`} replace />;
    }
    if (overview?.surfaceMode === "onboarding" && !isPlatformStaff(auth.permissions)) {
      return <Navigate to="/onboard" replace />;
    }
    return <Navigate to={`/w/${auth.workspace.slug}`} replace />;
  }

  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner aria-label={t("bootstrap.loading")} />
      </div>
    );
  }

  const first = workspaces?.[0];
  if (first) {
    return <Navigate to={`/w/${first.slug}`} replace />;
  }
  return <Navigate to="/no-workspace" replace />;
}
